import { createIntl } from 'react-intl';

import { getRegionalFormat } from '@dynatrace-sdk/user-preferences';

import { intlCache } from '../intl-cache.js';

/** Max value for minimumFractionDigits and maximumFractionDigits (see Intl.NumberFormat) */
const MAX_DIGITS = 20;

/** Adjust precision in floating point numbers. */
export function adjustNumberDecimals(
  /** Input number that should be abbreviated. */
  value: number,
  /** Precision of the fraction amount. */
  precision?: number,
): string {
  const _precision = Number.isInteger(precision) ? precision : undefined;
  // Get the absolute value to base any further calculations.
  const calcValue = Math.abs(value);

  // Edge case if the calculated value is exactly 0
  if (calcValue === 0) {
    return '0';
  }

  // Get the minimum value for the provided maximum precision
  // examples:
  // given precision 3: 0.001
  // given precision 6: 0.000001
  const minValue = 1 / Math.pow(10, Math.max(_precision ?? 3, 0));

  // If the calculated value is smaller than the minValue,
  // we can return the minimum value instead.
  if (calcValue < minValue) {
    // If the min value is smaller than 0, return a negative
    // value, else add a less-than sign in front.
    return value < 0 ? `-${minValue}` : `< ${minValue}`;
  }

  // Determine the maximum fraction digits
  let maximumFractionDigits = 0;
  let minimumFractionDigits = 0;
  // If precision is defined, we use this as a maximumFractionDigits and minimumFractionDigits
  // and fallback to values that make some sense in their scale.
  // Use 3 fractionDigits for values under 1
  // Use 2 fractionDigits for values under 10
  // Use 1 fractionDigits for values under 100
  if (_precision !== undefined) {
    maximumFractionDigits = Math.min(_precision, MAX_DIGITS);
    minimumFractionDigits = Math.min(_precision, MAX_DIGITS);
  } else if (calcValue < 1) {
    maximumFractionDigits = 3;
  } else if (calcValue < 10) {
    maximumFractionDigits = 2;
  } else if (calcValue < 100) {
    maximumFractionDigits = 1;
  }

  // Make sure that there are no negative values in the maximumFractionDigit and minimumFractionDigit
  maximumFractionDigits = Math.max(maximumFractionDigits, 0);
  minimumFractionDigits = Math.max(minimumFractionDigits, 0);
  const intl = createIntl(
    { locale: getRegionalFormat(), messages: {} },
    intlCache,
  );
  const formatter = new Intl.NumberFormat(intl.locale, {
    maximumFractionDigits,
    minimumFractionDigits,
  });
  return formatter.format(value);
}
